import moment from 'moment';
import { DATE_FORMAT } from 'utils';
import { Session } from 'types/listings';
import useCancellationPolicy from 'hooks/useCancellationPolicy';
import { useBrandId } from 'AppProvider';
import useExchangeId from 'hooks/useExchangeId';
import useListingV3 from 'api/listing/useListingV3/useListingV3';
import { useSearchParamsObject } from 'utils/url';
import {
  deriveEffectiveTimeFrame,
  ExperienceVersion,
  isContinuousScheduleType,
  ScheduleType,
} from '@kouto/types';
import useSupportedLanguage from 'utils/data/language/useSupportedLanguage';
import {
  deriveContinuousSelection,
  selectOpenSpans,
  toSelectedSession,
} from 'utils/continuousSelection';
import addDays from 'utils/date/addDays';
import useFirstAvailableCollectionDate from 'api/firstAvailableDate/firstAvailableCollectionDate/useFirstAvailableCollectionDate';
import { useMemo } from 'react';
import useCollectionAvailableSessions, {
  PublicSessionsQueryParams,
} from './useCollectionAvailableSessions';

interface Props {
  collectionId?: string;
  groupId?: string;
  resourceId?: string;
  date?: string;
  time?: string;
  duration?: number;
  latest: boolean;
}

/**
 * How far ahead a day-based resource's free days are fetched from the start date, so the picker can
 * offer a stay of several nights without a request per night. Bounded by what the endpoint will price.
 */
const DAY_FRAME_LOOKAHEAD_DAYS = 30;

const useResourceBookingSessionSelectedData = ({
  collectionId,
  groupId,
  resourceId,
  date,
  time,
  duration,
  latest,
}: Props) => {
  const brandId = useBrandId();
  const { from } = useSearchParamsObject<{ from?: string }>();
  const languageCode = useSupportedLanguage();
  const { data: exchangeIds, isLoading: isLoadingExchangeId } = useExchangeId(
    collectionId,
    brandId,
  );
  const [listingId] = exchangeIds ?? [null];

  const {
    data: listing,
    isLoading: isLoadingListingRequest,
    error: listingError,
  } = useListingV3({
    listingId,
    version: latest ? ExperienceVersion.LATEST : ExperienceVersion.PUBLISHED,
    language: languageCode,
  });

  const isLoadingListing = isLoadingExchangeId || isLoadingListingRequest;

  // 2 - GET FIRST AVAILABLE DATE FOR COLLECTION
  const { data: firstAvailableDate, isLoading: isLoadingFirstAvailableDate } =
    useFirstAvailableCollectionDate({
      brandId,
      // The route value may be a slug; resolve it to the real collection UUID via the loaded listing
      // so this UUID-only endpoint never receives a slug. Defers the call until the listing resolves.
      resourceGroupCollectionId: listing?.resourceGroupCollectionId,
      isPrivate: listing?.isUnlisted ?? false,
      latest,
      // `from` is the landing page Browse date filter, carried over by the tile link.
      from: date || from || moment().format(DATE_FORMAT),
    });

  // 3 - GET RESOURCE GROUP FROM COLLECTION
  let resourceGroup = listing?.resourceGroups?.find(
    (group) => group.id === groupId,
  );

  const isMapped = useMemo(() => listing?.isMapped ?? false, [listing]);

  if (!resourceGroup && listing?.resourceGroups?.length === 1 && isMapped) {
    resourceGroup = listing.resourceGroups[0];
  }

  // 4 - GET RESOURCE FROM RESOURCE GROUP
  const resource = resourceGroup?.experiences?.find(
    (resource) => resource.id === resourceId,
  );

  // A continuously booked collection exposes free spans in place of sessions; the guest picks a
  // start and a length inside one. The collection's schedule type says whether it is booked that way
  // at all, and a group of a collection switched either way still is or is not, whatever rule it
  // carries. The rule that shapes the choices rides on the resource group, and says whether it counts
  // days or nights when the collection allows its frame, since a collection can hold one of each.
  const availabilityRule = resourceGroup?.availabilityRule ?? null;
  const isContinuous = isContinuousScheduleType(listing?.scheduleType);
  const timeFrame = deriveEffectiveTimeFrame(
    listing?.scheduleType,
    availabilityRule?.timeFrame,
  );

  // 5 - GET SESSION FROM API
  const sessionsProps: PublicSessionsQueryParams = { latest };
  // Skip sessions call if there is not date and firstAvailableDate is loading
  sessionsProps.skip = !date && isLoadingFirstAvailableDate;
  // A date in the URL is the day to show, so its sessions load alongside first-available-date rather
  // than after it; the first available date only picks the day when the URL has none.
  const sessionsDate =
    date ||
    firstAvailableDate?.firstAvailableDate ||
    moment().format(DATE_FORMAT);
  if (collectionId && listing && (date || firstAvailableDate)) {
    sessionsProps.collectionId = listing.resourceGroupCollectionId;
    sessionsProps.from = sessionsDate;
    // A stay of several nights needs the nights after the start date too. Read from the collection,
    // not the group, so the range is right before a group is picked.
    sessionsProps.to =
      listing.scheduleType === ScheduleType.MULTI_DAY
        ? addDays(sessionsDate, DAY_FRAME_LOOKAHEAD_DAYS)
        : sessionsDate;
    if (resourceGroup) {
      sessionsProps.resourceGroupId = resourceGroup.id;
      if (resource) {
        sessionsProps.experienceId = resource.id;
      }
    }
  }
  const {
    hasLoadedSessions,
    sessions: rawSessions,
    isLoadingSessions,
  } = useCollectionAvailableSessions(sessionsProps);
  const sessions = isContinuous ? selectOpenSpans(rawSessions) : rawSessions;

  // 6 - GET SELECTED SESSION, IF POSSIBLE
  // The URL's choice when it is still free, else the earliest free choice; a session either way, so
  // the rest of the flow (tiers, capacity, the cart) reads it as it always has.
  const continuousSelection =
    isContinuous && availabilityRule && timeFrame
      ? deriveContinuousSelection({
          date: sessionsDate,
          duration,
          durations: resourceGroup?.durations ?? [],
          experienceId: resource?.id,
          rule: availabilityRule,
          spans: sessions,
          time,
          timeFrame,
        })
      : null;

  let selectedSession: Session | undefined;
  if (isContinuous) {
    // Never the session path: a continuous resource has spans, and a group with no rule yet has no
    // availability to pick from.
    selectedSession = continuousSelection
      ? toSelectedSession(continuousSelection)
      : undefined;
  } else if (sessions.length > 0) {
    let filteredSessions = [...sessions];
    if (
      time &&
      filteredSessions.find(
        (session) => session.startDateTime.split('T')[1] === time,
      )
    ) {
      filteredSessions = filteredSessions.filter(
        (session) => session.startDateTime.split('T')[1] === time,
      );
    }
    if (
      duration &&
      filteredSessions.find((session) => session.duration === duration)
    ) {
      filteredSessions = filteredSessions.filter(
        (session) => session.duration === duration,
      );
    }
    if (filteredSessions.length === 1) {
      selectedSession = filteredSessions[0];
    }
  }
  if (!selectedSession && resource && !isContinuous) {
    selectedSession = sessions[0];
  }

  // 7 - GET CANCELLATION POLICY FROM EVENT OR BRAND SETTINGS
  const { cancellationPolicy, isLoading: isLoadingCancellationPolicy } =
    useCancellationPolicy(listing?.cancellationPolicyId);

  const isLoading =
    isLoadingListing ||
    isLoadingSessions ||
    isLoadingCancellationPolicy ||
    isLoadingFirstAvailableDate;

  // A page's first paint waits for what it cannot draw without: the listing and a first sessions
  // response. Every later request — a new date, a new group — keeps the previous rows on screen while
  // the next arrive, so a click never swaps the page for a skeleton. `isLoading` keeps saying a request
  // is in flight, for the URL clean-up and date fallbacks that must not act on rows about to change.
  const isInitialLoading = isLoading && (!listing || !hasLoadedSessions);

  return {
    isInitialLoading,
    isLoading,
    isLoadingListing,
    isLoadingSessions,
    isLoadingCancellationPolicy,
    listingError,
    listing,
    resourceGroup,
    resource,
    sessions,
    selectedSession,
    availabilityRule,
    continuousSelection,
    isContinuous,
    timeFrame,
    cancellationPolicy,
    firstAvailableDate,
  };
};

export default useResourceBookingSessionSelectedData;
